import { EPSILON } from "../../../constants";
import { InvalidArgument, InvalidArgumentsLength } from "../../../exception";
import type { Point } from "../../../types";
import { LeastSquares } from "../../../utils";
import type {
  EwaReversePixelMapper,
  PartialDerivatives,
} from "../../EwaReversePixelMapper";
import type { ReversePixelMapper } from "../../ReversePixelMapper";
import type { ReversePixelMapperFactory } from "../../ReversePixelMapperFactory";

/**
 * Polynomial Distortion
 *
 * First two coefficients are used to hole global polynomial information
 *  c0 = Order of the polynomial being created
 *  c1 = number_of_terms in one polynomial equation
 *
 * Rest of the coefficients map to the equations....
 *    v = c0 + c1*x + c2*y + c3*x*y + c4*x^2 + c5*y^2 + c6*x^3 + ...
 * for each control point.
 * As such total coefficients =  2 + number_terms * 2
 *
 * Polynomial Distortion Notes:
 * + Order 1.5 is fudged to map into a bilinear distortion.
 *   though it is not the same order as that distortion.
 *
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l02631
 */
export class Polynomial implements ReversePixelMapper, EwaReversePixelMapper {
  /**
   * @inheritDoc
   */
  isConstantPartialDerivatives = false;

  /**
   * Polynomial constructor.
   *
   * @param coeffs
   */
  constructor(readonly coeffs: number[]) {}

  /**
   * @inheritDoc
   */
  reverseMap(x: number, y: number): Point {
    const n = this.coeffs[1];
    let sx = 0,
      sy = 0;

    for (let k = 0; k < n; k++) {
      const basis = polyBasisFn(k, x, y);
      sx += basis * this.coeffs[2 + k];
      sy += basis * this.coeffs[2 + k + n];
    }

    return [sx, sy];
  }

  /**
   * @inheritDoc
   */
  getValidity(): 1 {
    return 1;
  }

  /**
   * @inheritDoc
   */
  getPartialDerivatives(x: number, y: number): PartialDerivatives {
    const n = this.coeffs[1];
    let dux = 0,
      duy = 0,
      dvx = 0,
      dvy = 0;

    for (let k = 0; k < n; k++) {
      const basisDx = polyBasisDx(k, x, y);
      const basisDy = polyBasisDy(k, x, y);
      const indexX = 2 + k;
      const indexY = indexX + n;
      dux = basisDx * this.coeffs[indexX];
      duy = basisDy * this.coeffs[indexX];
      dvx = basisDx * this.coeffs[indexY];
      dvy = basisDy * this.coeffs[indexY];
    }

    return [dux, duy, dvx, dvy];
  }
}

/**
 * Polynomial distortion factory.
 *
 * First input argument is polynomial order.
 * Rest input Arguments are sets of control points.
 * order [u,v, x,y] ...
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l01094
 */
export class PolynomialFactory implements ReversePixelMapperFactory {
  create(args: number[]): Polynomial {
    const termsCount = numTerms(args[0]);

    if (args.length < 1 + termsCount * 4) {
      throw new InvalidArgumentsLength(
        `Polynomial distortion of order ${
          args[0]
        } requires at least ${termsCount} control point pairs (1 + ${
          termsCount * 4
        } arguments)`
      );
    }

    const leastSquares = new LeastSquares(termsCount, 2);

    for (let i = 1; i < args.length; i += 4) {
      const terms: number[] = [];
      for (let j = 0; j < termsCount; j++) {
        terms[j] = polyBasisFn(j, args[i + 2], args[i + 3]);
      }
      leastSquares.addTerms(terms, args.slice(i, i + 2));
    }

    return new Polynomial(
      [args[0], termsCount].concat(leastSquares.solve().flat())
    );
  }
}

/**
 * Returns number of terms for a 2d polynomial
 *
 * @param order
 * @internal
 */
function numTerms(order: number): number {
  if (
    order < 1 ||
    order > 5 ||
    (order != Math.floor(order) && order - 1.5 > EPSILON)
  ) {
    /* invalid polynomial order */
    throw new InvalidArgument(`Invalid polynomial order: ${order}`);
  }
  return Math.floor(((order + 1) * (order + 2)) / 2);
}

/**
 * Returns result for given polynomial term
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00153
 *
 * @param n
 * @param x
 * @param y
 * @internal
 */
function polyBasisFn(n: number, x: number, y: number): number {
  switch (n) {
    case 0:
      return 1.0; /* constant */
    case 1:
      return x;
    case 2:
      return y; /* affine          order = 1   terms = 3 */
    case 3:
      return x * y; /* bilinear        order = 1.5 terms = 4 */
    case 4:
      return x * x;
    case 5:
      return y * y; /* quadratic       order = 2   terms = 6 */
    case 6:
      return x * x * x;
    case 7:
      return x * x * y;
    case 8:
      return x * y * y;
    case 9:
      return y * y * y; /* cubic         order = 3   terms = 10 */
    case 10:
      return x * x * x * x;
    case 11:
      return x * x * x * y;
    case 12:
      return x * x * y * y;
    case 13:
      return x * y * y * y;
    case 14:
      return y * y * y * y; /* quartic     order = 4   terms = 15 */
    case 15:
      return x * x * x * x * x;
    case 16:
      return x * x * x * x * y;
    case 17:
      return x * x * x * y * y;
    case 18:
      return x * x * y * y * y;
    case 19:
      return x * y * y * y * y;
    case 20:
      return y * y * y * y * y; /* quintic   order = 5   terms = 21 */
  }
  return 0; /* should never happen */
}

/**
 * Returns polynomial term for x derivative
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00209
 *
 * @param n
 * @param x
 * @param y
 * @internal
 */
function polyBasisDx(n: number, x: number, y: number): number {
  switch (n) {
    case 0:
      return 0.0; /* constant */
    case 1:
      return 1.0;
    case 2:
      return 0.0; /* affine      order = 1   terms = 3 */
    case 3:
      return y; /* bilinear    order = 1.5 terms = 4 */
    case 4:
      return x;
    case 5:
      return 0.0; /* quadratic   order = 2   terms = 6 */
    case 6:
      return x * x;
    case 7:
      return x * y;
    case 8:
      return y * y;
    case 9:
      return 0.0; /* cubic       order = 3   terms = 10 */
    case 10:
      return x * x * x;
    case 11:
      return x * x * y;
    case 12:
      return x * y * y;
    case 13:
      return y * y * y;
    case 14:
      return 0.0; /* quartic     order = 4   terms = 15 */
    case 15:
      return x * x * x * x;
    case 16:
      return x * x * x * y;
    case 17:
      return x * x * y * y;
    case 18:
      return x * y * y * y;
    case 19:
      return y * y * y * y;
    case 20:
      return 0.0; /* quintic     order = 5   terms = 21 */
  }
  return 0.0; /* should never happen */
}

/**
 * Returns polynomial term for y derivative.
 * @see https://imagemagick.org/api/MagickCore/distort_8c_source.html#l00237
 *
 * @param n
 * @param x
 * @param y
 * @internal
 */
function polyBasisDy(n: number, x: number, y: number): number {
  switch (n) {
    case 0:
      return 0.0; /* constant */
    case 1:
      return 0.0;
    case 2:
      return 1.0; /* affine      order = 1   terms = 3 */
    case 3:
      return x; /* bilinear    order = 1.5 terms = 4 */
    case 4:
      return 0.0;
    case 5:
      return y; /* quadratic   order = 2   terms = 6 */
    default:
      return polyBasisDx(n - 1, x, y); /* weird but true */
  }
  /* NOTE: the only reason that last is not true for 'quadratic'
     is due to the re-arrangement of terms to allow for 'bilinear'
  */
}
